import {FC, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import { Topic } from '~src/interfaces/topic.interface';
import ItemDetailsHeader from '~src/shared/headers/itemDetalsHeader/ItemDetailsHeader';
import TranslatableSectionForm from '~src/shared/form/translatableSectionForm/TranslatableSectionForm';
import { translateContent } from '~src/services/translationServices';
import {
  archiveTopic,
  deleteTopic,
  editTopicPatch,
  fetchTopicQuestions,
  TopicCreate, unarchiveTopic
} from '~src/modules/topics/actions/topicActions';
import logger from '~src/services/logger/LlsLogger';
import { nameDescrFields } from '~src/shared/form/translatableSectionForm/defaultFields';
import { TranslatableField } from '~src/shared/form/translatableSectionForm/interfaces';
import {
  ApiReduxResp,
  getLoggingContext,
  isSuccess, ThunkAnyAction,
  ThunkMWDispatch
} from '~src/services/apiMiddleware';
import { StoreI } from '~src/rootReducer';
import { routes } from '~src/routingConfig';
import {useHistory} from "react-router";
import {useTranslation} from "react-i18next";
import {ErrResp} from "~src/shared/errors/interfaces";
import {MenuItemConfI} from "~src/shared/headers/itemDetalsHeader/types";

interface Props {
  topic: Topic;
}

export const TopicHeader: FC<Props>= ({ topic }) => {
  const [isEditFrmVisible, setIsEditFrmVisible] = useState(false);
  const [deletionError, setDeletionError] = useState<string|null>(null);

  const { t } = useTranslation();
  const history = useHistory<{fromTopicsListings?: boolean}>();

  const dispatch = useDispatch<ThunkMWDispatch>();

  const loadingEdit = useSelector((s: StoreI) => s.topics.loadingEdit);
  const loadingDel = useSelector((s: StoreI) => s.topics.loadingDel);
  const editTopicSections = useMemo(() => {
    const editTopicFields = [
      {
        value: topic.name,
      },
      {
        value: topic.description,
      },
    ].reduce((acc: TranslatableField[], field, i) => {
      acc[i] = {
        ...nameDescrFields[i],
        ...field,
      };
      return acc;
    }, []);

    return [
      {
        name: 'info',
        fields: editTopicFields,
      },
    ];
  }, [topic]);

  const onEditSubmit = (payload: TopicCreate) =>
    dispatch(editTopicPatch(topic.id, payload)).then((response) => {
      if (isSuccess(response)) {
        dispatch(fetchTopicQuestions(topic.id)).catch((e: unknown) => logger.error('error editing topic : ' + String(e)));
        toggleEditFrm();
      } else {
        logger.info('Error editing Topic', getLoggingContext(response, 'TopicHeader'));
      }
      return response;
    });

  const deleteThisTopic: () => ApiReduxResp<void> = () =>
    /* @ts-expect-error no idea actually what the problem is, something in dispatch typing I guess */
    dispatch(deleteTopic(topic.id, topic.name)).then((resp: ThunkAnyAction<void, ErrResp>) => {
      if (isSuccess(resp)) {
        goToList();
      } else {
        logger.error('Error deleting Topic', getLoggingContext(resp, 'TopicHeader'));
        setDeletionError("Error! Can't delete this topic.");
      }

      return resp;
    });

  const toggleEditFrm = (isVisible = false) => {
    setIsEditFrmVisible(isVisible || false);
  };

  const goToList = () => {
    if (history.location.state && history.location.state.fromTopicsListings) {
      history.goBack();
    } else history.push(routes.contentTopics.topics);
  };

  const additionalMenuItems: MenuItemConfI[] = useMemo(() => {
    const archive: MenuItemConfI = {
      txtKey: 'x.item.details.header.archive',
      onClick: () => dispatch(archiveTopic(topic.id)),
      iconName: "downArrow"
    }
    const unarchive: MenuItemConfI = {
      txtKey: 'x.item.details.header.unarchive',
      onClick: () => dispatch(unarchiveTopic(topic.id)),
      iconName: "upArrow",
    };
    return [topic.archived ? unarchive : archive];
  }, [topic.archived, topic.id, dispatch]);

  return (
    <ItemDetailsHeader
      t={t}
      isEditFrmVisible={isEditFrmVisible}
      deleteItem={deleteThisTopic}
      loadingDel={loadingDel}
      onBackClick={goToList}
      toggleEditFrm={toggleEditFrm}
      backArrow={{
        key: 'x.topics.details.header.return_link_title',
        txt: t('x.topics.details.header.return_link_title'),
      }}
      supTitle={{
        key: 'x.topics.details.header.topic_type, x.topics.list.types.types.XXX',
        txt: `${t('x.topics.details.header.topic_type')}: ${t(`x.topics.list.types.${topic.subject}`)}`,
      }}
      title={{ key: 'be', txt: translateContent(topic.name) }}
      description={{ key: 'be', txt: translateContent(topic.description) }}
      isFramework={topic.framework}
      framework={{
        key: 'x.topics.details.box.framework',
        txt: t('x.topics.details.box.framework'),
      }}
      frameworkTxt={{
        key: 'x.topic.details.header.menu.no_delete_topic',
        txt: t('x.topic.details.header.menu.no_delete_topic'),
      }}
      menuEdit={{
        key: 'x.attributes.details.header.menu.edit_topic',
        txt: t('x.attributes.details.header.menu.edit_topic'),
      }}
      menuDel={{
        key: 'x.attributes.details.header.menu.delete_attribute',
        txt: t('x.attributes.details.header.menu.delete_attribute'),
      }}
      additionalMenuItems={additionalMenuItems}
      deletionTitleKey="x.topic.delete_confirmation.headline"
      deletionContentKey="x.topic.delete_confirmation.content"
      deletionError={deletionError}
    >
      <TranslatableSectionForm
        t={t}
        titleKey="be"
        title={translateContent(topic.name)}
        form={`editTopic_${topic.id}`}
        sections={editTopicSections}
        onSubmit={onEditSubmit}
        onCancel={toggleEditFrm}
        loadingSave={loadingEdit}
        inContainer
        hideButtons
      />
    </ItemDetailsHeader>
  );
};

export default TopicHeader;
